import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Bookmark, Save, Loader2, Pencil, Trash2, Settings2, Pin } from "lucide-react";
import { C } from "./reportsUi";

export default function SavedViewsBar({ current, onLoad, registerSaver }) {
  const [views, setViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/reports/center/views").then(({ data }) => setViews(data.views || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (registerSaver) registerSaver(() => { setName(""); setSaveOpen(true); }); }, [registerSaver]);

  const save = async () => {
    if (!name.trim()) { toast.error("Name your view."); return; }
    setSaving(true);
    try {
      await api.post("/reports/center/views", { name: name.trim(), tab: current.tab, filters: current.filters, builder: current.tab === "builder" ? current.builder : null });
      toast.success("View saved."); setSaveOpen(false); setName(""); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const rename = async (v) => {
    const nn = window.prompt("Rename view", v.name);
    if (!nn || !nn.trim()) return;
    try { await api.patch(`/reports/center/views/${v.id}`, { name: nn.trim() }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (v) => {
    if (!window.confirm(`Delete view "${v.name}"?`)) return;
    try { await api.delete(`/reports/center/views/${v.id}`); setViews((x) => x.filter((i) => i.id !== v.id)); toast.success("Deleted."); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const togglePin = async (v) => {
    try {
      if (v.pinned) { await api.delete(`/reports/center/views/${v.id}/pin`); toast.success("Unpinned from Command Center."); }
      else { await api.post(`/reports/center/views/${v.id}/pin`); toast.success("Pinned to Command Center."); }
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const pinnedCount = views.filter((v) => v.pinned).length;

  return (
    <div className="flex items-center gap-1.5">
      <Select value="__none__" onValueChange={(id) => { const v = views.find((x) => x.id === id); if (v) onLoad(v); }}>
        <SelectTrigger data-testid="rc-views-load" className="h-9 w-[170px] text-[13px]"><Bookmark size={13} className="mr-1 shrink-0" style={{ color: C.navy }} /><SelectValue placeholder="Saved views" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__" disabled>{views.length ? "Load a saved view…" : "No saved views yet"}</SelectItem>
          {views.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button data-testid="rc-views-save" size="sm" variant="outline" onClick={() => { setName(""); setSaveOpen(true); }} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Save size={14} /> Save view</Button>
      {views.length > 0 && <Button data-testid="rc-views-manage" size="icon" variant="outline" onClick={() => setManageOpen(true)} className="h-9 w-9 border-[#D6E0E7]" style={{ color: C.navy }}><Settings2 size={15} /></Button>}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm" data-testid="rc-view-save-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Save current view</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>View name</Label>
            <Input data-testid="rc-view-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Kansas City · this month · leads" autoFocus />
            <p className="text-[11.5px]" style={{ color: C.slate }}>Saves the current tab, filters{current.tab === "builder" ? ", report type, columns and chart type" : ""}. Loading a view re-applies them (RBAC still applies).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button data-testid="rc-view-save-confirm" disabled={saving} onClick={save} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1">{saving && <Loader2 className="animate-spin" size={14} />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-md" data-testid="rc-view-manage-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Manage saved views</DialogTitle></DialogHeader>
          <p className="text-[11.5px] -mt-1" style={{ color: C.slate }} data-testid="rc-pinned-count">
            <Pin size={11} className="inline mr-1" style={{ color: C.navy }} />{pinnedCount}/5 pinned to Command Center
          </p>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {views.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-2 rounded-lg border p-2.5" style={{ borderColor: C.line }}>
                <div className="min-w-0"><div className="text-[13px] font-medium flex items-center gap-1.5" style={{ color: C.navy }}>{v.name}{v.pinned && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" style={{ background: "rgba(28,67,88,0.10)", color: C.navy }} data-testid={`rc-view-pinned-badge-${v.id}`}><Pin size={9} /> Pinned</span>}</div><div className="text-[11px]" style={{ color: C.slate }}>{v.tab}</div></div>
                <div className="flex items-center gap-1">
                  <button onClick={() => togglePin(v)} title={v.pinned ? "Unpin from Command Center" : "Pin to Command Center"} className="p-1.5" style={{ color: v.pinned ? C.navy : C.slate }} data-testid={`rc-view-pin-${v.id}`}><Pin size={14} fill={v.pinned ? "currentColor" : "none"} /></button>
                  <button onClick={() => { onLoad(v); setManageOpen(false); }} className="text-[12px] px-2 py-1 rounded" style={{ color: C.navy }}>Load</button>
                  <button onClick={() => rename(v)} className="p-1.5" style={{ color: C.slate }}><Pencil size={14} /></button>
                  <button onClick={() => del(v)} className="p-1.5 text-[#9C1E22]"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
